/*
  LOCAL TESTING ONLY: stands in for Auth0 Universal Login when
  REACT_APP_LOCAL_AUTH === 'true'. Rendered by LocalAuthProvider at /local-login.
*/
import React, { FC, FormEvent, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Layout, PrimaryButton } from '@components'
import styles from './local-login.module.scss'

const SAMPLE_EMAILS = [
  'jordan.rivera@example.com',
  'jordan.r@example.com',
  'someone.else@example.com'
]

type LocalLoginProps = {
  currentEmail: string | null
  onSignIn: (email: string, returnTo: string) => Promise<void>
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const LocalLogin: FC<LocalLoginProps> = ({ currentEmail, onSignIn }) => {
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') || '/'
  const isSignup = searchParams.get('screen_hint') === 'signup'

  const [email, setEmail] = useState(currentEmail || '')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submit = async (value: string) => {
    const candidate = value.trim()
    if (!EMAIL_PATTERN.test(candidate)) {
      setError('Enter an email address.')
      return
    }

    setError('')
    setSubmitting(true)
    try {
      await onSignIn(candidate, returnTo)
    } catch (err) {
      setSubmitting(false)
      setError(
        `Couldn't get a token from ${
          process.env.REACT_APP_LOCAL_AUTH_URL || 'http://localhost:8080'
        }. Is the local harness running? (${(err as Error)?.message || err})`
      )
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit(email)
  }

  return (
    <Layout hasFooter context="local-login">
      <div className={styles.container} data-testid="local-login">
        <p className={styles.banner}>
          Local test sign-in. No real Auth0 account is used.
        </p>
        <h2 className={styles.title}>
          {isSignup ? 'Create an account' : 'Sign in'}
        </h2>
        <p className={styles.hint}>
          Type or choose any email. The same email always maps to the same test
          account (alias.mp).
        </p>
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <label htmlFor="local-login-email" className={styles.label}>
            Email
          </label>
          <input
            id="local-login-email"
            className={styles.input}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          <PrimaryButton
            context="local-login-submit"
            className={styles.button}
            caption="Continue"
            isLoading={submitting}
            onClick={() => submit(email)}
          />
        </form>
        <div className={styles.samples}>
          {SAMPLE_EMAILS.map((sample) => (
            <button
              key={sample}
              type="button"
              className={styles.sample}
              disabled={submitting}
              onClick={() => {
                setEmail(sample)
                submit(sample)
              }}
            >
              {sample}
            </button>
          ))}
        </div>
        <p className={styles.hint}>
          After sign-in you go back to <code>{returnTo}</code>.
        </p>
      </div>
    </Layout>
  )
}

export default LocalLogin
